import type { ConceptChipApi } from '@mathflat/domain/@entities/ConceptChip/api'
import { Entity as ConceptChipEntity } from '@mathflat/domain/@entities/ConceptChip/dto'
import type { Student } from 'src/dto'

import achievementAnalysisApi from '@/api/achievementAnalysis.api'
import { makeAdapter } from '@/controller/Adapter.ts'
import {
  AchievementReport,
  AchievementReportColl,
} from '@/dto/AchievementAnalysis/AchievementReport.dto.ts'
import CheckableMap from '@/models/CheckableMap.ts'

class AchievementAnalysisController {
  adapter = {
    report: makeAdapter({
      from(data: res.AchievementReport & { studentName: Student['name'] }): AchievementReport {
        return new AchievementReport(data)
      },
    }),

    groupAchievementConceptChips: {
      from(
        data: ConceptChipApi.Response.ByGroupConceptChip[],
      ): ConceptChipEntity.AchievementConceptChip[] {
        return data
          .map(
            (item) =>
              new ConceptChipEntity.AchievementConceptChip({
                id: item.conceptChipId,
                achievementLevel: item.averageAchievementLevel,
                ...item,
              }),
          )
          .sort((a, b) => a.orderingNumber - b.orderingNumber)
      },
    },

    groupAchievementConceptChipDetail: {
      from(
        data: ConceptChipApi.Response.GroupConceptChipDetail,
      ): ConceptChipEntity.GroupAchievementConceptChipDetail {
        return new ConceptChipEntity.GroupAchievementConceptChipDetail(data)
      },
    },
  }

  /// 그룹 유형 분석

  getGroupConceptChipsAndAchievements = async (
    params: ConceptChipApi.Request.ByGroupConceptChipsParams,
  ) => {
    const { data } = await achievementAnalysisApi.getGroupConceptChipsAndAchievements(params)
    return this.adapter.groupAchievementConceptChips.from(data)
  }

  getGroupConceptChipDetail = async (
    params: ConceptChipApi.Request.GetGroupConceptChipDetailParams,
  ) => {
    const { data } = await achievementAnalysisApi.getGroupConceptChipDetail(params)
    return this.adapter.groupAchievementConceptChipDetail.from(data)
  }

  getGroupPastAchievements = async (
    params: ConceptChipApi.Request.GetGroupPastAchievementsParams,
  ) => {
    const { data } = await achievementAnalysisApi.getGroupPastAchievements(params)
    return this.adapter.groupAchievementConceptChips.from(data)
  }

  getGroupPersonalizedWeakConceptChip = async (
    params: ConceptChipApi.Request.GetGroupPersonalizedWeakConceptChipParams,
  ) => {
    const { data } = await achievementAnalysisApi.getGroupPersonalizedWeakConceptChip(params)
    return data
  }

  postGroupPersonalizedWeakConceptChip = async (
    params: ConceptChipApi.Request.PostGroupPersonalizedWeakConceptChipParams,
  ) => {
    return achievementAnalysisApi.postGroupPersonalizedWeakConceptChip(params)
  }

  getGroupCommonWeakConceptChip = async (
    params: ConceptChipApi.Request.GetGroupCommonWeakConceptChipParams,
  ) => {
    const { data } = await achievementAnalysisApi.getGroupCommonWeakConceptChip(params)
    return data
  }

  /// 개인 유형 분석

  getConceptChips = async (params: req.AchievementAnalysisChip) => {
    const { data } = await achievementAnalysisApi.getConceptChips(params)
    return data
  }

  getAchievements = async (params: req.Achievement, studentId: Student['id']) => {
    const { data } = await achievementAnalysisApi.getAchievements(params, studentId)
    return data
  }

  postAchievements = async (data: req.CohortAchievement) => {
    const { data: resData } = await achievementAnalysisApi.postAchievements(data)
    return resData
  }

  getPastAchievements = async (params: req.Achievement, studentId: Student['id']) => {
    const { data } = await achievementAnalysisApi.getPastAchievements(params, studentId)
    return data
  }

  getAchievementStudyLogs = async (
    studentId: res.Student['id'],
    conceptChipId: res.ConceptChip['conceptChipId'],
    params?: req.AchievementStudyLog,
  ) => {
    const {
      data: { data },
    } = await achievementAnalysisApi.getAchievementStudyLogs(studentId, conceptChipId, params)

    return data
  }

  createReport = async (studentId: res.Student['id'], payload: req.AchievementReport) => {
    const {
      data: { data },
    } = await achievementAnalysisApi.postAchievementReport(studentId, payload)
    return data
  }

  getAchievementReport = async (studentId: res.Student['id']) => {
    const {
      data: { data },
    } = await achievementAnalysisApi.getAchievementReport(studentId)

    const parsedData = data.reportInfo.map((item) =>
      this.adapter.report.from({ ...item, studentName: data.studentName }),
    )
    const reportMap = CheckableMap.fromArr(parsedData, 'id')
    return new AchievementReportColl(reportMap)
  }

  deleteReports = async (reportIds: AchievementReport['id'][]) => {
    await achievementAnalysisApi.deleteReports({ reportIds })
  }

  async preview(params: Pick<req.DownloadAchievementReport, 'reportId'>) {
    const { data } = await achievementAnalysisApi.getReportFile({
      ...params,
      presignedUrlType: 'PREVIEW',
    })
    return data
  }

  postLessonClassChallenges = async (params: req.LessonClassChallenge) => {
    const res = await achievementAnalysisApi.postLessonClassChallenges(params)
    return res.data
  }

  getLessonClassChallenges = async (lessonClassId: req.LessonClassChallenge['lessonClassId']) => {
    return await achievementAnalysisApi.getLessonClassChallenges(lessonClassId)
  }

  patchLessonClassChallengeStatus = async (params: {
    id: res.LessonClassChallengeInfo['id']
    status: 'ACTIVE' | 'INACTIVE'
  }) => {
    return await achievementAnalysisApi.patchLessonClassChallengeStatus(params)
  }
}

export const achievementAnalysisController = new AchievementAnalysisController()
